/**
 * SoleadLab 2.0 — Detection Ghost Popup (P2a fault-detection review)
 *
 * Floating review card for one ghost candidate, opened from MapCanvas's
 * detection-ghost layer click. Mirrors IndicationPreview's floating-card
 * positioning (project ring → screen, flip when cramped) and reuses its
 * exported PhotoSection for the nearest-closeup display.
 *
 * Actions:
 *   - Accept  → acceptGhostAsIndication (creation-core defaults, built WITH
 *     the ghost ring as geometry; topology derives from that real ring).
 *     markAccepted with the id.
 *   - Reject  → markRejected; MapCanvas's sync drops the ghost from the map.
 *   - Link to existing → shown ONLY when a same-type indication sits within
 *     ~2.5 m; appends this detection as a new OBSERVATION on the chosen fault
 *     (classify-panel link pattern: deriveObservations + top-level mirror +
 *     resolved→open auto-reopen), then markAccepted with that fault's id.
 *
 * Type default: similar-mode ghosts default to the scan's indication type
 * (resolved via the candidate's exemplar indication, falling back to
 * summary.indicationType); anomaly ghosts REQUIRE an explicit choice.
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useProjectStore, useUIStore, INDICATION_TYPES } from '../../stores';
import type {
  Indication,
  IndicationObservation,
  IndicationProperties,
  IndicationType,
} from '../../stores/projectStore';
import { useDetectionStore } from '../../stores/detectionStore';
import { acceptGhostAsIndication } from '../../utils/detectionAccept';
import { centroidOfPolygon, haversineM, indCenter } from '../../utils/geometry';
import { resolveImageSrc } from '../../utils';
import { deriveObservations } from '../../utils/observations';
import { captureDay } from '../../utils/captureDate';
import { driveThumbnailUrl } from '../../utils/driveImport';
import { PhotoSection } from '../editor/IndicationPreview';
import './DetectionGhostPopup.css';

/** Pixel gap between arrow tip and ghost edge (IndicationPreview convention). */
const ARROW_GAP = 14;
/** Auto-show the nearest linked closeup photo within this radius. */
const NEAR_PHOTO_RADIUS_M = 30;
/** "Link to existing" offered only for same-type faults within this radius. */
const LINK_RADIUS_M = 2.5;

// Accept builds the indication WITH the ghost ring (utils/detectionAccept) —
// the old create-at-centroid-then-override flow and its _version retry are
// gone; shared with DetectionPanel's bulk accept.

/** Compact location label for a link-target row (topology, else title/id). */
function linkTargetLabel(ind: Indication): string {
  const p = ind.properties;
  const topo = [
    p.station && `St ${p.station}`,
    p.inverter && `Inv ${p.inverter}`,
    p.string && `Str ${p.string}`,
    p.module && `Mod ${p.module}`,
  ]
    .filter(Boolean)
    .join(' · ');
  if (topo) return topo;
  if (p.title && p.title !== ind.type) return p.title;
  return `#${ind.id.slice(-6)}`;
}

interface DetectionGhostPopupProps {
  candId: string;
  map: MapboxMap;
  onClose: () => void;
}

export default function DetectionGhostPopup({
  candId,
  map,
  onClose,
}: DetectionGhostPopupProps) {
  const cand = useDetectionStore((s) =>
    s.candidates.find((c) => c.id === candId),
  );
  const job = useDetectionStore((s) => s.job);
  const summary = useDetectionStore((s) => s.summary);
  const markAccepted = useDetectionStore((s) => s.markAccepted);
  const markRejected = useDetectionStore((s) => s.markRejected);

  const indications = useProjectStore((s) => s.data.indications);
  const orthophotos = useProjectStore((s) => s.data.orthophotos);
  const updateIndication = useProjectStore((s) => s.updateIndication);
  const addNotification = useUIStore((s) => s.addNotification);

  const ghostCenter = useMemo(
    () => (cand ? centroidOfPolygon(cand.ring) : null),
    [cand],
  );

  // ── Similar-mode type default: exemplar's type, else summary verbatim ──
  const defaultType = useMemo((): IndicationType | '' => {
    if (!cand || job?.scanMode !== 'similar') return '';
    const exemplar = cand.props.exemplarId
      ? indications.find((i) => i.id === cand.props.exemplarId)
      : undefined;
    if (exemplar) return exemplar.type;
    const st = (summary as Record<string, unknown> | null)?.indicationType;
    if (typeof st === 'string' && st in INDICATION_TYPES) {
      return st as IndicationType;
    }
    return '';
  }, [cand, job?.scanMode, indications, summary]);

  const [typeChoice, setTypeChoice] = useState<IndicationType | ''>(defaultType);

  // ── Floating-card position (IndicationPreview pattern: flip when cramped) ──
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [flipped, setFlipped] = useState(false);

  const updatePosition = useCallback(() => {
    if (!cand) return;
    const cardHeight = cardRef.current?.offsetHeight ?? 300;
    let minY = Infinity;
    let maxY = -Infinity;
    let sumX = 0;
    for (const coord of cand.ring) {
      const pt = map.project(coord);
      if (pt.y < minY) minY = pt.y;
      if (pt.y > maxY) maxY = pt.y;
      sumX += pt.x;
    }
    const centerX = sumX / cand.ring.length;
    const needsFlip = minY - ARROW_GAP < cardHeight + 10;
    setFlipped(needsFlip);
    setPos(
      needsFlip
        ? { x: centerX, y: maxY + ARROW_GAP }
        : { x: centerX, y: minY - ARROW_GAP },
    );
  }, [cand, map]);

  useEffect(() => {
    updatePosition();
    map.on('move', updatePosition);
    return () => {
      try {
        map.off('move', updatePosition);
      } catch {
        /* map removed */
      }
    };
  }, [map, updatePosition]);

  // Close on Escape (IndicationPreview convention).
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // ── Nearest linked closeup photo within ~30 m (auto-shown) ──
  const nearestPhoto = useMemo(() => {
    if (!ghostCenter) return null;
    let best: Indication | null = null;
    let bestDist = Infinity;
    for (const ind of indications) {
      if (ind.type !== 'Photo' || !ind.properties.image) continue;
      const c = indCenter(ind.geometry as GeoJSON.Geometry);
      if (!c) continue;
      const d = haversineM(ghostCenter, c);
      if (d < bestDist) {
        bestDist = d;
        best = ind;
      }
    }
    if (!best || bestDist > NEAR_PHOTO_RADIUS_M) return null;
    return { photo: best, dist: bestDist };
  }, [ghostCenter, indications]);

  // ── Same-type link targets within ~2.5 m ──
  const linkMatches = useMemo(() => {
    if (!ghostCenter || !typeChoice) return [];
    const out: { ind: Indication; dist: number }[] = [];
    for (const ind of indications) {
      if (ind.type !== typeChoice) continue;
      const c = indCenter(ind.geometry as GeoJSON.Geometry);
      if (!c) continue;
      const d = haversineM(ghostCenter, c);
      if (d <= LINK_RADIUS_M) out.push({ ind, dist: d });
    }
    return out.sort((a, b) => a.dist - b.dist);
  }, [ghostCenter, typeChoice, indications]);

  // PhotoSection plumbing (reused from IndicationPreview, V-01C export).
  const thermalImgRef = useRef<HTMLImageElement>(null);
  const noop = useCallback(() => {}, []);

  if (!cand || !pos) return null;

  const scorePct = Math.round(cand.props.score01 * 100);

  // The scan's evidence day: the scanned ortho layer's capture day, else today.
  const scanDay = (() => {
    const raw = orthophotos.find((o) => o.id === job?.orthoLayerId)?.captureDate;
    const day = raw ? captureDay(raw) : '';
    return day || new Date().toISOString().split('T')[0];
  })();

  const handleAccept = () => {
    if (!typeChoice || !ghostCenter) return;
    // Built WITH the ghost ring — topology derives from the real geometry
    // (the old create-at-centroid-then-swap left fields describing a
    // location the mark no longer occupied).
    const id = acceptGhostAsIndication(cand, typeChoice);
    if (!id) return; // share-viewer defense in depth inside the core
    markAccepted(cand.id, id);
    addNotification({
      type: 'success',
      title: `${INDICATION_TYPES[typeChoice].label} added · ${scorePct}% match`,
      duration: 2000,
    });
    onClose();
  };

  const handleReject = () => {
    markRejected(cand.id);
    onClose();
  };

  const handleLink = (target: Indication) => {
    const now = new Date().toISOString();
    // Classify-panel link pattern: append an observation, mirror the LATEST
    // observation's date top-level, auto-reopen a resolved fault (it's still
    // present) as a client-stamped system event in the same single write.
    const newObs: IndicationObservation = { date: scanDay };
    const props: Partial<IndicationProperties> = {
      observations: [...deriveObservations(target), newObs],
      captureDate: scanDay,
    };
    if (target.properties.status === 'resolved') {
      props.status = 'open';
      props.statusEvents = [
        ...(target.properties.statusEvents ?? []),
        {
          status: 'open',
          at: now,
          by: null,
          note: `Re-observed by detection scan on ${scanDay}`,
        },
      ];
    }
    updateIndication(target.id, { properties: props });
    // Register the (possibly new) scan day with the date filter.
    useProjectStore.getState().updateAvailableDates();
    markAccepted(cand.id, target.id);
    addNotification({
      type: 'success',
      title: 'Linked as new observation',
      duration: 2000,
    });
    onClose();
  };

  // Nearest-photo imagery resolution (IndicationPreview conventions).
  const photoProps = nearestPhoto?.photo.properties;
  const photoSrc = photoProps?.image ? resolveImageSrc(photoProps.image) : '';
  const photoThermal = photoProps?.thermalData;
  const hasThermalOverlay =
    photoThermal &&
    ((photoThermal.rois?.length ?? 0) > 0 ||
      (photoThermal.points?.length ?? 0) > 0);

  const typeColor = typeChoice ? INDICATION_TYPES[typeChoice].color : '#a855f7';

  return (
    <div
      ref={cardRef}
      className={`detection-ghost-popup${flipped ? ' detection-ghost-popup--flipped' : ''}`}
      style={{ left: pos.x, top: pos.y }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      data-testid="detection-ghost-popup"
    >
      <div className="detection-ghost-popup__inner">
        {/* Header — suggested-type selector (IndicationPopup/Preview pattern) + match % */}
        <div className="detection-ghost-popup__header">
          <span
            className="detection-ghost-popup__type-dot"
            style={{ background: typeColor }}
          />
          <select
            className="detection-ghost-popup__type-select"
            value={typeChoice}
            onChange={(e) => setTypeChoice(e.target.value as IndicationType | '')}
            data-testid="ghost-type-select"
          >
            {typeChoice === '' && <option value="">Choose type…</option>}
            {Object.entries(INDICATION_TYPES).map(([type, config]) => (
              <option key={type} value={type}>
                {config.label}
              </option>
            ))}
          </select>
          <span
            className="detection-ghost-popup__score"
            title={`Detection score ${cand.props.score01.toFixed(3)} (${cand.props.mode})`}
          >
            {scorePct}% match
          </span>
        </div>

        {/* Nearest linked closeup — auto-shown within ~30 m, graceful note otherwise */}
        {nearestPhoto && photoSrc ? (
          <>
            <div className="detection-ghost-popup__photo-caption">
              Closeup {Math.round(nearestPhoto.dist)} m away
            </div>
            <PhotoSection
              imageSrc={photoSrc}
              thermalData={photoThermal}
              hasThermalOverlay={hasThermalOverlay}
              thermalImgRef={thermalImgRef}
              noop={noop}
              visualImage={
                photoProps?.visualDriveId
                  ? driveThumbnailUrl(photoProps.visualDriveId)
                  : photoProps?.visualImage
                    ? resolveImageSrc(photoProps.visualImage)
                    : undefined
              }
              zoomImage={
                photoProps?.zoomDriveId
                  ? driveThumbnailUrl(photoProps.zoomDriveId)
                  : photoProps?.zoomImage
                    ? resolveImageSrc(photoProps.zoomImage)
                    : undefined
              }
            />
          </>
        ) : (
          <div className="detection-ghost-popup__no-photo">
            No nearby closeup photo (within {NEAR_PHOTO_RADIUS_M} m)
          </div>
        )}

        {/* Link to existing — only when a same-type fault sits within ~2.5 m */}
        {linkMatches.length > 0 && (
          <div className="detection-ghost-popup__links" data-testid="ghost-link-list">
            <div className="detection-ghost-popup__links-label">
              Link to existing {INDICATION_TYPES[typeChoice as IndicationType]?.label}
            </div>
            {linkMatches.map(({ ind, dist }) => (
              <button
                key={ind.id}
                type="button"
                className="detection-ghost-popup__link-item"
                onClick={() => handleLink(ind)}
                title="Append this detection as a new observation of the existing fault"
              >
                <span className="detection-ghost-popup__link-label">
                  {linkTargetLabel(ind)}
                </span>
                <span className="detection-ghost-popup__link-dist">
                  {dist.toFixed(1)} m
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="detection-ghost-popup__actions">
          <button
            type="button"
            className="detection-ghost-popup__btn detection-ghost-popup__btn--primary"
            onClick={handleAccept}
            disabled={!typeChoice}
            title={typeChoice ? undefined : 'Choose a type first'}
            data-testid="ghost-accept"
          >
            Accept
          </button>
          <button
            type="button"
            className="detection-ghost-popup__btn detection-ghost-popup__btn--danger"
            onClick={handleReject}
            data-testid="ghost-reject"
          >
            Reject
          </button>
          <button
            type="button"
            className="detection-ghost-popup__btn"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
